import React, { useRef, useState, useEffect } from "react";
import "./GramSection.css";

const GramSection = ({ videos = [] }) => {
  const containerRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const videoRefs = useRef([]);
  const lastVolume = useRef([]);
  const [playingIndex, setPlayingIndex] = useState(null);
  const [mutedMap, setMutedMap] = useState({});

  const fallback = [
    {
      src: "https://res.cloudinary.com/demo/video/upload/sample.mp4",
      caption: "Sample video"
    }
  ];

  const list = videos && videos.length ? videos : fallback;
  const pages = Math.max(1, list.length); // positions = one per item (shift by 1)
  const itemFullWidth = useRef(0);

  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;

    // compute item full width (item width + gap)
    const first = c.querySelector(".gram-item");
    if (first) {
      const itemW = first.offsetWidth;
      const gapStr = getComputedStyle(c).columnGap || getComputedStyle(c).gap || "12px";
      const gap = parseFloat(gapStr) || 12;
      itemFullWidth.current = itemW + gap;
    } else {
      itemFullWidth.current = c.offsetWidth / 2; // fallback
    }

    const onScroll = () => {
      const full = itemFullWidth.current || c.offsetWidth;
      const page = Math.round(c.scrollLeft / full);
      setCurrentPage(((page % pages) + pages) % pages);
    };

    c.addEventListener("scroll", onScroll, { passive: true });
    return () => c.removeEventListener("scroll", onScroll);
  }, [pages]);

  // auto-advance pages every 8 seconds; keeps all videos muted
  useEffect(() => {
    if (pages <= 1) return;
    const timer = setTimeout(() => {
      const next = (currentPage + 1) % pages;
      scrollToPage(next);
      // play leftmost visible item while keeping everything muted
      playMuted(next);
    }, 8000);

    return () => clearTimeout(timer);
  }, [currentPage, pages]);

  // helper: play index while muting all videos
  const playMuted = (index) => {
    videoRefs.current.forEach((vid, idx) => {
      if (!vid) return;
      if (idx === index) {
        vid.muted = true;
        vid.play().catch(() => {});
      } else {
        vid.muted = true;
        if (!vid.paused) vid.pause();
      }
    });
    setPlayingIndex(index);
    // update muted map: everything is muted
    const newMap = {};
    videoRefs.current.forEach((_, idx) => (newMap[idx] = true));
    setMutedMap(newMap);
  };

  // autoplay leftmost item on mount (muted)
  useEffect(() => {
    setTimeout(() => {
      playMuted(0);
    }, 200);
  }, []);

  const scrollToPage = (idx) => {
    const c = containerRef.current;
    if (!c) return;
    const full = itemFullWidth.current || c.offsetWidth;
    c.scrollTo({ left: idx * full, behavior: "smooth" });
  };

  return (
    <div className="gram-section">
      <h2 className="gram-title">From the Gram</h2>
      <div className="gram-carousel-wrapper">
        <div className="gram-grid" ref={containerRef}>
          {list.map((v, i) => (
            <div className="gram-item" key={i}>
              <div className="video-wrap">
                <video
                  ref={(el) => (videoRefs.current[i] = el)}
                  src={typeof v === "string" ? v : v.src}
                  poster={(typeof v === "object" && v.poster) || ""}
                  muted
                  // remove native controls and disable built-in features
                    controls={false}
                    controlsList="nodownload noremoteplayback nofullscreen"
                    disablePictureInPicture
                  loop
                  playsInline
                  onContextMenu={(e) => e.preventDefault()}
                    onPlay={() => setPlayingIndex(i)}
                    onLoadedMetadata={() => {
                      const vid = videoRefs.current[i];
                      if (!vid) return;
                      if (lastVolume.current[i] == null) lastVolume.current[i] = 0.6;
                      // initialize volume and muted map if not present
                      vid.volume = lastVolume.current[i];
                      if (mutedMap[i] == null) setMutedMap((m) => ({ ...m, [i]: vid.muted || false }));
                    }}
                  onPause={() => {
                    if (playingIndex === i) setPlayingIndex(null);
                  }}
                  className="gram-video"
                />

                <button
                  className={`gram-play-btn ${playingIndex === i ? "playing" : ""}`}
                  onClick={() => {
                    const vid = videoRefs.current[i];
                    if (!vid) return;
                    if (vid.paused) {
                      // pause others
                      videoRefs.current.forEach((other, idx) => {
                        if (other && idx !== i && !other.paused) other.pause();
                      });
                      vid.play().catch(() => {});
                      setPlayingIndex(i);
                    } else {
                      vid.pause();
                      setPlayingIndex(null);
                    }
                  }}
                  aria-label={playingIndex === i ? "Pause video" : "Play video"}
                >
                  {playingIndex === i ? "❚❚" : "▶"}
                </button>
                <button
                  className={`gram-volume-btn ${mutedMap[i] ? "muted" : ""}`}
                  onClick={() => {
                    const vid = videoRefs.current[i];
                    if (!vid) return;
                    vid.muted = true;
                    setMutedMap((m) => ({ ...m, [i]: true }));
                  }}
                  aria-label="Muted"
                >
                  🔈
                </button>
              </div>

              {v.caption && (
                <div className="gram-caption">
                  {typeof v === "object" && v.user ? (
                    <>
                      <div className="caption-user">@{v.user}</div>
                      <div className="caption-text">{v.text || v.caption}</div>
                    </>
                  ) : (
                    <div className="caption-text">{v.caption}</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="gram-dots" role="tablist" aria-label="Carousel pages">
        {Array.from({ length: pages }).map((_, idx) => (
          <button
            key={idx}
            className={`gram-dot ${idx === currentPage ? "active" : ""}`}
            onClick={() => scrollToPage(idx)}
            aria-label={`Go to page ${idx + 1}`}
            aria-selected={idx === currentPage}
          />
        ))}
      </div>
    </div>
  );
};

export default GramSection;
